/**
 * `memberapp/calendar/calendarModel.ts` — the calendar's PURE read
 * model (#1665, design §5.3.1 + §9.4).
 *
 * Everything here is a pure function over payload shapes so the views
 * stay thin and the model is unit-testable without mounting anything.
 *
 * Data reality this encodes (§5.3.1):
 *   * Rostered rows are date + role — clock times are DERIVED server-
 *     side (`window_*` fields, #1665). The fields are ADDITIVE: this
 *     module must tolerate their absence entirely (PWA-cached pre-#1665
 *     payload, or an SPA deploy racing ahead of the backend) — absent ⇒
 *     the all-day lane, never a fabricated hour. Every derived window
 *     renders with an "est." label (§5.3.7).
 *   * Leave/MC context is the client-side `useMyLeaves` union — only
 *     `leave` and `mc` expand into absence spans (the same rule
 *     `MeThisWeekTable.buildSyntheticRows` applies).
 *   * Months can be legitimately partial (release regions): the
 *     "TBD — planned" ribbon derives from the long-range `cycles`
 *     metadata where available; where it isn't, the calendar simply
 *     makes no claim (§5.3.2 — never fabricate certainty).
 */

import { type WeekStart, addDaysIso, weekStartOf } from "../../lib/rosterWeeks";
import { type ShiftCategoryFilter, matchesShiftFilter } from "../../lib/shiftCategories";
import type { TimeblockDayItem } from "./timeblockModel";

// ── Payload mirrors (hand-typed per repo convention — see
//    `PersonalRoster.tsx`; `schema.gen.ts` stays the wire truth). ──────

export type WindowSource = "station" | "team_sessions" | "default" | "none";

export interface CalendarAssignment {
  date: string;
  role: string;
  session: string | null;
  team_id: string;
  team_name: string;
  cycle_id: string;
  membership_id: string;
  assignment_id?: string;
  is_published: boolean;
  hours?: number | null;
  override_reason_codes?: string[] | null;
  override_reason_note?: string | null;
  swap_history?: { from_display_name?: string | null; to_display_name?: string | null }[];
  note?: string | null;
  takeover?: unknown | null;
  kind?: string | null;
  is_oncall?: boolean | null;
  is_weekend?: boolean | null;
  assignment_reason?: { code: string; headline: string; detail?: string | null } | null;
  /** #1665 §9.4 — derived window. Optional + nullable BY CONTRACT. */
  window_start_minute?: number | null;
  window_end_minute?: number | null;
  window_source?: WindowSource | null;
  /**
   * #1909 (flows finding 33) — CLIENT-DERIVED by `buildDayEvents`, never on
   * the wire: the member is off sick (an active MC on this team) on this
   * row's day, so the seat is released for cover — the lead's roster counts
   * it empty and the ladder is looking for someone to take it. The row stays
   * on the member's roster until someone does, but it must not read as a
   * shift they are expected at.
   */
  released_for_cover?: boolean;
}

export interface LeaveWindow {
  start_date: string;
  end_date: string;
  request_type: string;
  /** #1667 — the ledger row id, when the caller has it (opens the
   *  request detail from the absence sheet). Optional by contract. */
  id?: string | undefined;
  /** #1667 — §8 lifecycle status; absent on an OLD backend payload. */
  status?: string | undefined;
  /** #1909 — the team the row belongs to (an MC releases only that team's
   *  seats). Absent means it is read as covering every team on those days. */
  team_id?: string | undefined;
  /** #1909 (finding 23) — set when the row is the day the ladder gave back
   *  as rest after the recall on this date. */
  rest_after_recall_date?: string | null | undefined;
}

/** Long-range cycle metadata (`useScheduleWithHorizon` `cycles[]`). */
export interface CycleSpanMeta {
  cycle_id: string;
  cycle_label: string;
  starts_at: string;
  ends_at: string;
  completeness_phase?: "nothing_published" | "on_call_published" | "fully_published" | null;
}

// ── Derived windows ──────────────────────────────────────────────────

export interface DerivedWindow {
  startMinute: number;
  /** May exceed 1440 — a cross-midnight span anchored on the row's date. */
  endMinute: number;
  source: Exclude<WindowSource, "none">;
}

/**
 * The one tolerance point for the §9.4 fields: absent, null, or
 * `source === "none"` all mean "no derivable window" → all-day lane.
 * This is load-bearing for deploy ordering (SPA ships before the
 * backend stamp exists) — never widen it to guess an hour.
 */
export function windowOf(row: CalendarAssignment): DerivedWindow | null {
  const source = row.window_source;
  if (source == null || source === "none") return null;
  const start = row.window_start_minute;
  const end = row.window_end_minute;
  if (typeof start !== "number" || typeof end !== "number") return null;
  if (end <= start) return null;
  return { startMinute: start, endMinute: end, source };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `510` → `"8:30"`; minutes past midnight wrap (cross-midnight ends). */
export function formatMinute(minute: number): string {
  const wrapped = ((minute % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${h}:${pad2(m)}`;
}

/** `"8:00–13:00"` — the range every derived window renders as (+ "est."). */
export function formatWindowRange(window: DerivedWindow): string {
  return `${formatMinute(window.startMinute)}–${formatMinute(window.endMinute)}`;
}

// ── Role display ─────────────────────────────────────────────────────

const STATION_SESSION_SUFFIXES = ["FULL_DAY", "FULLDAY", "NIGHT", "AM", "PM"] as const;

export interface RoleDisplay {
  /** "Obs Ward" — the human station/duty label. */
  station: string;
  /** "AM" | "PM" | "Full day" | "Night" | null. */
  session: string | null;
  isOnCall: boolean;
}

function titleCaseToken(token: string): string {
  return token
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w[0]?.toUpperCase() + w.slice(1)))
    .join(" ");
}

/** Human display for a persistence role token (solver grammar aware). */
export function roleDisplay(role: string): RoleDisplay {
  if (role.startsWith("STATION_")) {
    const body = role.slice("STATION_".length);
    const upper = body.toUpperCase();
    for (const suffix of STATION_SESSION_SUFFIXES) {
      if (upper.endsWith(`_${suffix}`) && body.length > suffix.length + 1) {
        const name = body.slice(0, -(suffix.length + 1));
        const session =
          suffix === "AM" || suffix === "PM" ? suffix : suffix === "NIGHT" ? "Night" : "Full day";
        return { station: titleCaseToken(name), session, isOnCall: false };
      }
    }
    return { station: titleCaseToken(body), session: null, isOnCall: false };
  }
  if (role.startsWith("ONCALL_")) {
    return {
      station: `On-call ${titleCaseToken(role.slice("ONCALL_".length))}`,
      session: null,
      isOnCall: true,
    };
  }
  if (
    ["ONCALL", "EXT", "WEEKEND_EXT", "WCONSULT", "WEEKEND_CONSULT"].includes(role.toUpperCase())
  ) {
    return { station: "On-call", session: null, isOnCall: true };
  }
  return { station: titleCaseToken(role), session: null, isOnCall: false };
}

/**
 * §5.3.2 — month-cell abbreviation: initials for multi-word names,
 * 3-glyph truncation for single words (the same shape the member
 * abbreviation generator produces).
 */
export function stationAbbrev(stationLabel: string): string {
  const words = stationLabel.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "—";
  if (words.length === 1) return (words[0] as string).slice(0, 3);
  return words
    .slice(0, 3)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();
}

/**
 * The compact chip code for an on-call station, from its own name. The
 * display label is "On-call <name>", and abbreviating that gave every call
 * the same "OR" (R1, R2, R3, R-CICU — 30 Sep re-click), so the code skips
 * the "On-call" prefix: a short name (≤ 4) is its own code (`R1`); several
 * words give their initials (`Night Float` → `NF`); a dashed name gives its
 * longest part (`R-CICU` → `CICU`); a long single word its first four.
 */
export function onCallAbbrev(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "—";
  if (trimmed.length <= 4) return trimmed.toUpperCase();
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length > 1) return stationAbbrev(trimmed);
  const parts = trimmed.split(/[-–—/]+/).filter(Boolean);
  const longest = parts.reduce((a, b) => (b.length > a.length ? b : a), "");
  return (longest || trimmed).slice(0, 4).toUpperCase();
}

/**
 * What a seat's bar says (month cells, the week's all-day lane) — #2172 M1,
 * founder 11 Oct 2026: the seat's own words, never a cut role code (the
 * Night call read "NIGH"). A station seat is its name and session ("55 AM",
 * "Obs Ward PM", "Clinic Full day"); an on-call seat is the call's own name
 * ("Night", "R1", "R-CICU", "Night Float"). Where the bar is too narrow,
 * `fitLabelToWidth` shortens it at a word boundary with "…".
 */
export function seatBarLabel(role: string): string {
  if (role.startsWith("ONCALL_")) {
    const name = titleCaseToken(role.slice("ONCALL_".length));
    if (name) return name;
  }
  const display = roleDisplay(role);
  return display.session ? `${display.station} ${display.session}` : display.station;
}

/**
 * The longest whole-word head of `label` that fits `maxWidth` (measured by
 * `measure`), with "…" when words were dropped. A label that fits, or an
 * unknown width (≤ 0), comes back whole; when even the first word does not
 * fit it comes back as that word + "…" and the bar's CSS clips the rest.
 */
export function fitLabelToWidth(
  label: string,
  maxWidth: number,
  measure: (text: string) => number,
): string {
  if (!(maxWidth > 0) || measure(label) <= maxWidth) return label;
  const words = label.split(/\s+/).filter(Boolean);
  for (let n = words.length - 1; n >= 1; n--) {
    const head = `${words.slice(0, n).join(" ")}…`;
    if (measure(head) <= maxWidth) return head;
  }
  return words.length > 1 ? `${words[0]}…` : label;
}

/**
 * The compact chip code for a role token: an on-call station by its own
 * name (`ONCALL_R2` → `R2`), anything else by its station label
 * (`stationAbbrev`). The calendar's bars no longer use it (#2172 M1 —
 * `seatBarLabel`).
 */
export function roleAbbrev(role: string): string {
  if (role.startsWith("ONCALL_")) {
    const name = titleCaseToken(role.slice("ONCALL_".length));
    if (name) return onCallAbbrev(name);
  }
  return stationAbbrev(roleDisplay(role).station);
}

// ── Month grid ───────────────────────────────────────────────────────

export interface MonthGridCell {
  iso: string;
  inMonth: boolean;
  isWeekend: boolean;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The query param the Calendar tab opens on (`/me?date=2026-11-03`). */
export const CALENDAR_DATE_PARAM = "date";

/** True for a bare `YYYY-MM-DD` day that parses — what `?date=` accepts. */
export function isIsoDay(value: string | null | undefined): value is string {
  if (typeof value !== "string" || !ISO_DAY.test(value)) return false;
  return !Number.isNaN(new Date(`${value}T00:00:00Z`).getTime());
}

/**
 * The member Calendar, opened on ONE day (30 Sep re-click).
 *
 * Every "View in Calendar ›" / "View on calendar ›" link used to be a bare
 * `/me`, which opens on today — so a card about Tue 3 Nov dropped the member
 * on 30 Sep and left them to page there. `CalendarTab` reads the param once,
 * moves the selection + the viewed month/week/day to it, and drops it from the
 * URL. Anything that is not a usable day falls back to plain `/me`.
 */
export function calendarDayHref(iso: string | null | undefined): string {
  const day = typeof iso === "string" ? iso.slice(0, 10) : null;
  return isIsoDay(day) ? `/me?${CALENDAR_DATE_PARAM}=${day}` : "/me";
}

export function monthStartIso(anchorIso: string): string {
  return `${anchorIso.slice(0, 7)}-01`;
}

export function addMonthsIso(anchorIso: string, delta: number): string {
  const year = Number(anchorIso.slice(0, 4));
  const month = Number(anchorIso.slice(5, 7));
  const next = new Date(Date.UTC(year, month - 1 + delta, 1));
  return next.toISOString().slice(0, 10);
}

export function monthTitle(anchorIso: string): string {
  const year = Number(anchorIso.slice(0, 4));
  const month = Number(anchorIso.slice(5, 7));
  const d = new Date(Date.UTC(year, month - 1, 1));
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
}

// ── The header row's title (#2172 M1) ────────────────────────────────

/**
 * What the Calendar's one header row names, in two lengths: `long` when it
 * fits beside the pager, the view switch and ⋯; `short` when it does not
 * (a 360-pt phone, a long month name). The header measures and picks one —
 * it never shrinks the type.
 */
export interface CalendarHeaderTitle {
  long: string;
  short: string;
}

function utcDay(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

const DAY_MONTH: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
};

/** "12 Oct – 18 Oct" / "12–18 Oct" (the locale's own order and range form). */
function weekHeaderTitle(anchorIso: string, weekStart: WeekStart): CalendarHeaderTitle {
  const startIso = weekStartOf(anchorIso, weekStart);
  const start = utcDay(startIso);
  const end = utcDay(addDaysIso(startIso, 6));
  const long = `${start.toLocaleDateString(undefined, DAY_MONTH)} – ${end.toLocaleDateString(undefined, DAY_MONTH)}`;
  const format = new Intl.DateTimeFormat(undefined, DAY_MONTH);
  const short = typeof format.formatRange === "function" ? format.formatRange(start, end) : long;
  return { long, short };
}

export function calendarHeaderTitle(
  view: "month" | "week" | "day",
  anchorIso: string,
  weekStart: WeekStart = "sun",
): CalendarHeaderTitle {
  if (view === "month") {
    return {
      long: monthTitle(anchorIso),
      short: utcDay(monthStartIso(anchorIso)).toLocaleDateString(undefined, {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }),
    };
  }
  if (view === "week") return weekHeaderTitle(anchorIso, weekStart);
  const day = utcDay(anchorIso);
  return {
    long: day.toLocaleDateString(undefined, { weekday: "short", ...DAY_MONTH }),
    short: day.toLocaleDateString(undefined, DAY_MONTH),
  };
}

/**
 * Cells covering the anchor's month (4–6 whole weeks), Monday first unless
 * `weekStart` says Sunday. `sixRows` (#2554) fills the grid to six rows with
 * the next month's days: the phone calendar's month always has six.
 */
export function monthGridCells(
  anchorIso: string,
  weekStart: WeekStart = "mon",
  sixRows = false,
): MonthGridCell[] {
  const first = monthStartIso(anchorIso);
  const nextMonthFirst = addMonthsIso(anchorIso, 1);
  const gridStart = weekStartOf(first, weekStart);
  const cells: MonthGridCell[] = [];
  let iso = gridStart;
  // Walk whole weeks until the week containing the month's last day ends.
  while (iso < nextMonthFirst || cells.length % 7 !== 0 || (sixRows && cells.length < 6 * 7)) {
    const dow = new Date(`${iso}T00:00:00Z`).getUTCDay();
    cells.push({
      iso,
      inMonth: iso >= first && iso < nextMonthFirst,
      isWeekend: dow === 0 || dow === 6,
    });
    iso = addDaysIso(iso, 1);
    if (cells.length > 6 * 7) break; // defensive: never more than 6 weeks
  }
  return cells;
}

// ── Day events (the merge) ───────────────────────────────────────────

export interface AbsenceSpan {
  /** "leave" | "mc" — the only request types that read as absences. */
  kind: string;
  startIso: string;
  endIso: string;
  /** #1667 — carried through from `LeaveWindow` when present. */
  id?: string | undefined;
  status?: string | undefined;
  /** #1909 (finding 23) — the recall a given-back rest day repays. */
  restAfterRecallIso?: string | undefined;
}

/**
 * What an absence is called on the calendar (#1909). A rest day the ladder
 * gave back after a recall is stored as a lead `leave` row, but it is not
 * leave the member asked for — it reads the way the Inbox card says it,
 * "given back as rest" (flows finding 23).
 */
export function absenceLabel(span: AbsenceSpan, clinical = true): string {
  // #2491 — "MC" only for a clinical team.
  if (span.kind === "mc") return clinical ? "Off sick (MC)" : "Off sick";
  if (span.restAfterRecallIso) return "Rest day · given back as rest";
  return "On leave";
}

/**
 * The month / week bar for an absence — its display label, short. #2172
 * M1 (founder, 11 Oct 2026): the bars read "Leave", "Off sick", "Rest day",
 * never the request type's own token ("leave", "MC").
 */
export function absenceChip(span: AbsenceSpan): string {
  if (span.kind === "mc") return "Off sick";
  if (span.restAfterRecallIso) return "Rest day";
  return "Leave";
}

/** What a seat released for cover says on the member's own calendar. */
export const RELEASED_FOR_COVER = "released for cover";

export interface DayEvents {
  shifts: CalendarAssignment[];
  absences: AbsenceSpan[];
  /** §9.4 typed personal layer (#1668) — the `░` outline vocabulary. */
  timeblocks: TimeblockDayItem[];
}

export interface DayEventOptions {
  filter?: ShiftCategoryFilter;
  showLeave?: boolean;
  /** §5.3.5 — the Personal layer toggle (#1668). Default shown. */
  showPersonal?: boolean;
  hiddenTeamIds?: ReadonlySet<string>;
}

const ABSENCE_TYPES = new Set(["leave", "mc"]);
/** An MC row with no `team_id` (old payload, test mock) covers every team. */
const ANY_TEAM = "*";

function eachDay(startIso: string, endIso: string): string[] {
  const out: string[] = [];
  let iso = startIso;
  let guard = 0;
  while (iso <= endIso && guard < 400) {
    out.push(iso);
    iso = addDaysIso(iso, 1);
    guard += 1;
  }
  return out;
}

/**
 * Merge schedule rows + leave windows into a per-day map for any view.
 * Shifts sort all-day first, then by window start; filtering follows
 * the #1605 chips + §5.3.5 layer/team toggles.
 */
export function buildDayEvents(
  assignments: readonly CalendarAssignment[],
  leaves: readonly LeaveWindow[],
  opts: DayEventOptions = {},
  timeblocksByDayMap?: ReadonlyMap<string, TimeblockDayItem[]>,
): Map<string, DayEvents> {
  const { filter = "all", showLeave = true, showPersonal = true, hiddenTeamIds } = opts;
  const map = new Map<string, DayEvents>();
  const dayOf = (iso: string): DayEvents => {
    let d = map.get(iso);
    if (!d) {
      d = { shifts: [], absences: [], timeblocks: [] };
      map.set(iso, d);
    }
    return d;
  };

  // #1909 (finding 33) — the days an active MC covers, per team. Read
  // whatever the leave layer toggle says: hiding the MC tint must not turn
  // a released seat back into a plain shift.
  const mcDays = new Set<string>();
  for (const leave of leaves) {
    if (leave.request_type !== "mc") continue;
    for (const iso of eachDay(leave.start_date, leave.end_date)) {
      mcDays.add(`${leave.team_id ?? ANY_TEAM}|${iso}`);
    }
  }
  const releasedForCover = (row: CalendarAssignment) =>
    mcDays.has(`${row.team_id}|${row.date}`) || mcDays.has(`${ANY_TEAM}|${row.date}`);

  for (const row of assignments) {
    if (hiddenTeamIds?.has(row.team_id)) continue;
    if (!matchesShiftFilter(row, filter)) continue;
    dayOf(row.date).shifts.push(releasedForCover(row) ? { ...row, released_for_cover: true } : row);
  }
  for (const events of map.values()) {
    events.shifts.sort((a, b) => {
      const wa = windowOf(a);
      const wb = windowOf(b);
      if (wa === null && wb === null) return a.role.localeCompare(b.role);
      if (wa === null) return -1; // all-day lane first
      if (wb === null) return 1;
      return wa.startMinute - wb.startMinute;
    });
  }

  if (showLeave) {
    for (const leave of leaves) {
      if (!ABSENCE_TYPES.has(leave.request_type)) continue;
      for (const iso of eachDay(leave.start_date, leave.end_date)) {
        dayOf(iso).absences.push({
          kind: leave.request_type,
          startIso: leave.start_date,
          endIso: leave.end_date,
          id: leave.id,
          status: leave.status,
          restAfterRecallIso: leave.rest_after_recall_date ?? undefined,
        });
      }
    }
  }

  // Personal blocks (#1668) — a typed layer of their own, never mixed
  // into `shifts`: the views draw them in the outline vocabulary and the
  // drag engine only ever binds this list (rostered rows refuse, D4).
  if (showPersonal && timeblocksByDayMap) {
    for (const [iso, items] of timeblocksByDayMap) {
      dayOf(iso).timeblocks = items;
    }
  }
  return map;
}

// ── Unreleased spans (the "TBD — planned" ribbon, §5.3.2) ────────────

export interface UnreleasedSpan {
  fromIso: string;
  /** Inclusive end. */
  toIso: string;
  /** true ⇒ on-call is out and only day-to-day is still being planned. */
  onCallOut: boolean;
  cycleLabel: string;
}

/**
 * Cycle spans not yet fully released, clipped to a visible range.
 * `cycles` comes from the long-range read where the team has it; teams
 * without it yield no spans — the calendar then simply makes no claim.
 */
export function unreleasedSpans(
  cycles: readonly CycleSpanMeta[],
  visibleFromIso: string,
  visibleToIso: string,
): UnreleasedSpan[] {
  const out: UnreleasedSpan[] = [];
  for (const cycle of cycles) {
    const phase = cycle.completeness_phase ?? "fully_published";
    if (phase === "fully_published") continue;
    // `ends_at` is exclusive in the payload (start + horizon_days).
    const lastIso = addDaysIso(cycle.ends_at, -1);
    const from = cycle.starts_at > visibleFromIso ? cycle.starts_at : visibleFromIso;
    const to = lastIso < visibleToIso ? lastIso : visibleToIso;
    if (from > to) continue;
    out.push({
      fromIso: from,
      toIso: to,
      onCallOut: phase === "on_call_published",
      cycleLabel: cycle.cycle_label,
    });
  }
  return out.sort((a, b) => a.fromIso.localeCompare(b.fromIso));
}
